import { apiRequest } from '@/lib/api/client';
import type {
  EditorAISuggestion,
  EditorCalendarEvent,
  EditorConnection,
  EditorDocumentTemplate,
  EditorJointArticle,
  EditorMessage,
  EditorNotebookNote,
  EditorObject,
  EditorObjectApproval,
  EditorObjectFile,
  EditorObjectParticipant,
  EditorObjectVersion,
  EditorOverviewStats,
  EditorProfile,
  EditorToolResult,
  EditorWorkspaceSummary,
} from './types';

export const getEditorSummary = (): Promise<EditorWorkspaceSummary> =>
  apiRequest<EditorWorkspaceSummary>('/editor/summary', { channel: 'platform' });

export const getEditorProfile = (): Promise<EditorProfile> =>
  apiRequest<EditorProfile>('/editor/profile', { channel: 'platform' });

export const updateEditorProfile = (payload: Partial<EditorProfile>): Promise<EditorProfile> =>
  apiRequest<EditorProfile>('/editor/profile', {
    method: 'PATCH',
    body: payload,
    channel: 'platform',
  });

export const getEditorStats = (): Promise<EditorOverviewStats> =>
  apiRequest<EditorOverviewStats>('/editor/stats', { channel: 'platform' });

export const getEditorObjects = (
  kind?: string,
  status?: string,
  search?: string
): Promise<EditorObject[]> =>
  apiRequest<EditorObject[]>('/editor/objects', {
    channel: 'platform',
    query: {
      kind,
      status,
      search,
    },
  });

export const getEditorObject = (id: string): Promise<EditorObject> =>
  apiRequest<EditorObject>(`/editor/objects/${id}`, { channel: 'platform' });

export const createEditorObject = (payload: Partial<EditorObject>): Promise<EditorObject> =>
  apiRequest<EditorObject>('/editor/objects', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const updateEditorObject = (
  id: string,
  payload: Partial<EditorObject> & { action_note?: string; actor_name?: string }
): Promise<EditorObject> =>
  apiRequest<EditorObject>(`/editor/objects/${id}`, {
    method: 'PATCH',
    body: payload,
    channel: 'platform',
  });

export const addEditorVersion = (
  objectId: string,
  payload: Partial<EditorObjectVersion>
): Promise<EditorObjectVersion> =>
  apiRequest<EditorObjectVersion>(`/editor/objects/${objectId}/versions`, {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const addEditorParticipant = (
  objectId: string,
  payload: Partial<EditorObjectParticipant>
): Promise<EditorObjectParticipant> =>
  apiRequest<EditorObjectParticipant>(`/editor/objects/${objectId}/participants`, {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const addEditorApproval = (
  objectId: string,
  payload: Partial<EditorObjectApproval>
): Promise<EditorObjectApproval> =>
  apiRequest<EditorObjectApproval>(`/editor/objects/${objectId}/approvals`, {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const getEditorTemplates = (): Promise<EditorDocumentTemplate[]> =>
  apiRequest<EditorDocumentTemplate[]>('/editor/templates', { channel: 'platform' });

export const getEditorConnections = (): Promise<EditorConnection[]> =>
  apiRequest<EditorConnection[]>('/editor/connections', { channel: 'platform' });

export const createEditorConnection = (
  payload: Partial<EditorConnection>
): Promise<EditorConnection> =>
  apiRequest<EditorConnection>('/editor/connections', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const getEditorMessages = (connectionId: string): Promise<EditorMessage[]> =>
  apiRequest<EditorMessage[]>(`/editor/connections/${connectionId}/messages`, {
    channel: 'platform',
  });

export const sendEditorMessage = (
  connectionId: string,
  payload: Partial<EditorMessage>
): Promise<EditorMessage> =>
  apiRequest<EditorMessage>(`/editor/connections/${connectionId}/messages`, {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const getEditorFiles = (objectId?: string): Promise<EditorObjectFile[]> =>
  apiRequest<EditorObjectFile[]>('/editor/files', {
    channel: 'platform',
    query: {
      object_id: objectId,
    },
  });

export const createEditorFile = (
  payload: Partial<EditorObjectFile>
): Promise<EditorObjectFile> =>
  apiRequest<EditorObjectFile>('/editor/files', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const deleteEditorFile = (fileId: string): Promise<{ success: boolean }> =>
  apiRequest<{ success: boolean }>(`/editor/files/${fileId}`, {
    method: 'DELETE',
    channel: 'platform',
  });

export const getEditorJointArticles = (): Promise<EditorJointArticle[]> =>
  apiRequest<EditorJointArticle[]>('/editor/joint-articles', { channel: 'platform' });

export const createEditorJointArticle = (
  payload: Partial<EditorJointArticle>
): Promise<EditorJointArticle> =>
  apiRequest<EditorJointArticle>('/editor/joint-articles', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const updateEditorJointArticle = (
  id: string,
  payload: Partial<EditorJointArticle>
): Promise<EditorJointArticle> =>
  apiRequest<EditorJointArticle>(`/editor/joint-articles/${id}`, {
    method: 'PATCH',
    body: payload,
    channel: 'platform',
  });

export const runEditorTool = (payload: {
  tool_type: string;
  title: string;
  input_text?: string;
  linked_object_code?: string;
}): Promise<EditorToolResult> =>
  apiRequest<EditorToolResult>('/editor/tools/run', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const getEditorCalendar = (): Promise<EditorCalendarEvent[]> =>
  apiRequest<EditorCalendarEvent[]>('/editor/calendar', { channel: 'platform' });

export const createEditorCalendarEvent = (
  payload: Partial<EditorCalendarEvent>
): Promise<EditorCalendarEvent> =>
  apiRequest<EditorCalendarEvent>('/editor/calendar', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const updateEditorCalendarEvent = (
  id: string,
  payload: Partial<EditorCalendarEvent>
): Promise<EditorCalendarEvent> =>
  apiRequest<EditorCalendarEvent>(`/editor/calendar/${id}`, {
    method: 'PATCH',
    body: payload,
    channel: 'platform',
  });

export const deleteEditorCalendarEvent = (id: string): Promise<{ success: boolean }> =>
  apiRequest<{ success: boolean }>(`/editor/calendar/${id}`, {
    method: 'DELETE',
    channel: 'platform',
  });

export const getEditorNotebook = (): Promise<EditorNotebookNote[]> =>
  apiRequest<EditorNotebookNote[]>('/editor/notebook', { channel: 'platform' });

export const createEditorNotebookNote = (
  payload: Partial<EditorNotebookNote>
): Promise<EditorNotebookNote> =>
  apiRequest<EditorNotebookNote>('/editor/notebook', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const updateEditorNotebookNote = (
  id: string,
  payload: Partial<EditorNotebookNote>
): Promise<EditorNotebookNote> =>
  apiRequest<EditorNotebookNote>(`/editor/notebook/${id}`, {
    method: 'PATCH',
    body: payload,
    channel: 'platform',
  });

export const deleteEditorNotebookNote = (id: string): Promise<{ success: boolean }> =>
  apiRequest<{ success: boolean }>(`/editor/notebook/${id}`, {
    method: 'DELETE',
    channel: 'platform',
  });

export const getEditorAISuggestion = (payload: {
  prompt: string;
  linked_object_code?: string;
  mode?: string;
}): Promise<EditorAISuggestion> =>
  apiRequest<EditorAISuggestion>('/editor/ai/suggest', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });
